import { UseMutationOptions, UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../enums/queryKeys';
import { MutationKeys } from '../../enums/mutationKeys';
import { NotificationTypes } from '../../enums/notificationTypes';
import {
  EditProfilePayload,
  EditProfileResponse,
  ProfileResponse,
  SetProfilePasswordPayload,
  SetProfilePasswordResponse,
  SubscriptionActionResponse,
} from '../../models/profile.model';
import {
  fetchProfile,
  saveProfile,
  saveProfilePassword,
  requestSubscriptionRenewal,
  requestNewSubscription,
  notifyMyProfileService,
  getAxiosErrorMessage,
} from './myProfileServiceUtils';
import { myProfileServiceMessages } from './myProfile.constants';

const useMyProfileService = () => {
  const queryClient = useQueryClient();

  const invalidateProfile = () => queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PROFILE] });

  const getProfile = (): UseQueryOptions<ProfileResponse, Error> => ({
    queryKey: [QueryKeys.GET_PROFILE],
    queryFn: fetchProfile,
  });

  const editProfile = (): UseMutationOptions<EditProfileResponse, Error, EditProfilePayload> => ({
    mutationKey: [MutationKeys.EDIT_PROFILE],
    mutationFn: saveProfile,
    onSuccess: (data) => {
      invalidateProfile();
      notifyMyProfileService(NotificationTypes.SUCCESS, data.message);
    },
    onError: () => notifyMyProfileService(NotificationTypes.ERROR, myProfileServiceMessages.EDIT_PROFILE_ERROR),
  });

  const setProfilePassword = (): UseMutationOptions<SetProfilePasswordResponse, Error, SetProfilePasswordPayload> => ({
    mutationKey: [MutationKeys.SET_PROFILE_PASSWORD],
    mutationFn: saveProfilePassword,
    onSuccess: (data) => notifyMyProfileService(NotificationTypes.SUCCESS, data.message),
    onError: () => notifyMyProfileService(NotificationTypes.ERROR, myProfileServiceMessages.SET_PASSWORD_ERROR),
  });

  const requestRenewal = (): UseMutationOptions<SubscriptionActionResponse, Error, void> => ({
    mutationKey: [MutationKeys.REQUEST_SUBSCRIPTION_RENEWAL],
    mutationFn: requestSubscriptionRenewal,
    onSuccess: (data) => {
      invalidateProfile();
      notifyMyProfileService(NotificationTypes.SUCCESS, data.message);
    },
    onError: (error) => notifyMyProfileService(NotificationTypes.ERROR, getAxiosErrorMessage(error)),
  });

  const requestSubscription = (): UseMutationOptions<SubscriptionActionResponse, Error, void> => ({
    mutationKey: [MutationKeys.REQUEST_SUBSCRIPTION],
    mutationFn: requestNewSubscription,
    onSuccess: (data) => {
      invalidateProfile();
      notifyMyProfileService(NotificationTypes.SUCCESS, data.message);
    },
    onError: (error) => notifyMyProfileService(NotificationTypes.ERROR, getAxiosErrorMessage(error)),
  });

  return {
    getProfile,
    editProfile,
    setProfilePassword,
    requestRenewal,
    requestSubscription,
  };
};

export default useMyProfileService;
